import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaEraser, FaFileExport, FaMagnifyingGlass, FaUserPlus } from "react-icons/fa6";
import Swal from "sweetalert2";
import "./employee-list.style.scss";
import "../../components/form-inputs/form-inputs.style.scss";
import "../../components/bill-list/bill-filter.style.scss";

// for filter form handle
import { useFormik } from "formik";
import * as yup from "yup";

// components
import TextField from "../../components/form-inputs/text-field.component";
import UserListItem from "../../components/user-list-item/user-list-item.component";
import AddEmployeeWizard from "../../components/user-profile-sidemodal/add-employee-wizard.component";
import EmployeeProfile from "../../components/user-profile-sidemodal/employee-profile.component";
import EmptyRecord from "../../components/empty-record/empty-record.component";
import Spinner from "../../components/spinner/spinner.component";
import FormError from "../../components/form-error/form-error.component";
import InlineNotice from "../../components/inline-notice/inline-notice.component";

// global function
import { roleAccess } from "../../global/function";

// queries
import {
	useEmployees,
	Employee,
	EmployeeFilters,
	EmployeePermissions,
} from "../../queries/employee.queries";

import { useExportFile } from "../../queries/export.queries";
import { useSession } from "../../queries/auth.queries";

const employeeSchema = yup.object().shape({
	user_id: yup.string().nullable(),
	first_name: yup.string().nullable(),
	last_name: yup.string().nullable(),
	user_mobile: yup.string().nullable(),
	user_email: yup.string().email("Please enter valid email").nullable(),
	username: yup.string().nullable(),
});

const initial_values = {
	user_id: "",
	first_name: "",
	last_name: "",
	user_email: "",
	user_mobile: "",
	username: "",
};

function EmployeeList() {
	const { data: session } = useSession();
	const exportMutation = useExportFile();

	const [addEmployeeView, setAddEmployeeView] = useState(false);
	// Set when "Complete Profile" is picked on the add-employee-wizard success
	// screen — opens EmployeeProfile straight into edit mode (startEditable)
	// for the record just created.
	const [completeProfileEmployee, setCompleteProfileEmployee] =
		useState<Employee | null>(null);
	// Set when the add-employee-wizard's search step hits a match that's
	// already in_company — nothing to add, so we open their profile directly
	// instead. Only the id is known at this point; EmployeeProfile hydrates
	// the rest itself via useEmployeeDetails.
	const [existingEmployeeId, setExistingEmployeeId] = useState<string | null>(
		null,
	);
	const [permissions, setPermissions] = useState<EmployeePermissions | undefined>(undefined);

	// Filters actually applied to the query — only updated on Search submit / Reset,
	// so typing in the filter form doesn't refetch on every keystroke.
	const [appliedFilters, setAppliedFilters] = useState<EmployeeFilters>({});

	const {
		data,
		error,
		isError,
		isPending,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
	} = useEmployees(appliedFilters);

	const employees = useMemo(
		() =>
			data?.pages.flatMap((page, pageIndex) =>
				page.rows.map((row) => ({ row, page: pageIndex + 1 }))
			) ?? [],
		[data]
	);

	// Infinite scroll: fetch the next page once the sentinel enters the viewport
	const sentinelRef = useRef(null);

	useEffect(() => {
		const node = sentinelRef.current;
		if (!node) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
					fetchNextPage();
				}
			},
			{ rootMargin: "200px" }
		);

		observer.observe(node);
		return () => observer.disconnect();
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	useEffect(() => {
		const getPermission = async () => {
			setPermissions(await roleAccess("employee"));
		};
		getPermission();
	}, []);

	const filterFormik = useFormik({
		validationSchema: employeeSchema,
		initialValues: initial_values,
		onSubmit: async (filterValues) => {
			setAppliedFilters(filterValues);
		},
		onReset: async () => {
			setAppliedFilters({});
		},
	});

	const handleUserExport = async () => {
		Swal.fire({
			title: "Export Users",
			text: "All system users will be exported to excel file. Are you sure?",
			icon: "info",
			showDenyButton: true,
			confirmButtonText: "Export",
			denyButtonText: "Cancel",
			customClass: {
				popup: "alert-modal",
				actions: "btn-container",
				confirmButton: "alert-btn btn-confirm",
				denyButton: "alert-btn btn-cancel",
			},
		}).then((result) => {
			if (result.isConfirmed && !exportMutation.isPending) {
				exportMutation.mutate({
					tableName: "user",
					companyId: session?.company_id,
				});
			}
		});
	};

	function _renderEmployeesFilter() {
		return (
			<div className="filter-container">
				<div className="filter-container-inner">
					<div className="filter-items-container">
						<h2 className="filter-section-header">
							Filter Employees
						</h2>
						<p className="action-description">
							The export to excel exports all users information.
						</p>
						<div
							className="action-btn"
							onClick={handleUserExport}
						>
							<FaFileExport />
							Export to excel
						</div>
						<FormError
							isError={exportMutation.isError}
							error={exportMutation.error}
							fallback="Failed to export file."
						/>
						<InlineNotice
							message={exportMutation.isSuccess ? "File downloaded" : null}
							type="success"
							autoHideMs={3000}
							onDismiss={() => exportMutation.reset()}
						/>
					</div>
					<div className="filter-items-container">
						<TextField
							name="user_id"
							label="Employee Id"
							placeholder="# Employee ID"
							formik={filterFormik}
						/>
						<div className="input-row">
							<TextField
								name="first_name"
								label="First Name"
								placeholder="Enter First Name"
								formik={filterFormik}
							/>
							<TextField
								name="last_name"
								label="Last Name"
								placeholder="Enter Last Name"
								formik={filterFormik}
							/>
						</div>
						<TextField
							name="user_mobile"
							label="Mobile Number"
							placeholder="Enter Mobile Number"
							formik={filterFormik}
						/>
						<TextField
							name="user_email"
							label="Employee Email"
							placeholder="Enter Email"
							formik={filterFormik}
						/>
						<TextField
							name="username"
							label="Username"
							placeholder="Enter Username"
							formik={filterFormik}
						/>
					</div>
					<div className="filter-btn-container">
						<div
							className="filter-btn inverted"
							onClick={() => filterFormik.resetForm()}
						>
							<FaEraser />Reset
						</div>
						<div
							className="filter-btn"
							onClick={filterFormik.submitForm}
						>
							<FaMagnifyingGlass />
							Search
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="employee-list-wrapper">
			<div className="wrapper-row">
				{_renderEmployeesFilter()}
				<div className="employee-list-container">
					<div className="section-header-container">
						<h2 className="section-header-title">Employees</h2>
						{permissions?.edit && (
							<div
								className="header-action-btn"
								onClick={() => setAddEmployeeView(true)}
							>
								<FaUserPlus />
								Add Employee
							</div>
						)}
					</div>
					<FormError
						isError={isError}
						error={error}
						fallback="Failed to load employees."
					/>
					<div className="employee-list-items">
						{!isPending && employees.length === 0 ? (
							<EmptyRecord />
						) : (
							employees.map(({ row, page }) => (
								<UserListItem
									key={row.user_id}
									user={row}
									type="employee"
									currentPageNum={page}
									empPermission={permissions}
								/>
							))
						)}
					</div>
					{(isPending || isFetchingNextPage) && <Spinner styles={undefined} />}
					<div ref={sentinelRef} />
				</div>
			</div>
			{addEmployeeView && (
				<AddEmployeeWizard
					isOpen={addEmployeeView}
					onClose={() => setAddEmployeeView(false)}
					onComplete={(employee) => setCompleteProfileEmployee(employee)}
					onExistingEmployeeFound={(userInfoId) =>
						setExistingEmployeeId(userInfoId)
					}
				/>
			)}
			{completeProfileEmployee && (
				<EmployeeProfile
					isEmployeeModalVisible={true}
					onEmployeeModalClose={() => setCompleteProfileEmployee(null)}
					employee={completeProfileEmployee}
					empPermission={permissions}
					startEditable
				/>
			)}
			{existingEmployeeId && (
				<EmployeeProfile
					isEmployeeModalVisible={true}
					onEmployeeModalClose={() => setExistingEmployeeId(null)}
					employee={{ user_info_id: existingEmployeeId }}
					empPermission={permissions}
				/>
			)}
		</div>
	);
}

export default EmployeeList;
